// Registry-backed navigation guard for PROGRAMMATIC navigators (BL-1669 AC 5,
// generalising BL-758 AC 10). `router.push` bypasses both `beforeunload` and
// `useUnsavedChangesGuard`'s anchor interception, so a button that navigates
// (the Sidebar's nav, the auction detail tab strip) consults the shared
// `unsaved-changes-registry` first and holds the target for a confirm dialog.
//
// One mechanism, not two: the auction Settings page used to carry its own
// dirty context (`settings-dirty-guard`) for the same job. Sub-pages arm the
// registry through `useUnsavedChangesGuard`; every programmatic navigator
// consults it through this hook.

'use client';

import { useCallback, useState } from 'react';
import { hasUnsavedChanges } from '@auctogon/admin-isomorphic/isomorphic/unsaved-changes-registry';

export interface UseGuardedNavigationResult<T> {
  /**
   * Attempt a navigation. Performs it immediately when nothing is armed;
   * otherwise holds `target` as `pending` for the caller's confirm dialog.
   */
  navigate: (target: T) => void;
  /** The held target, or `null` when nothing awaits confirmation. */
  pending: T | null;
  /** Perform the held navigation (the dialog's Discard). */
  confirmLeave: () => void;
  /** Drop the held navigation (the dialog's Cancel). */
  cancelLeave: () => void;
}

/**
 * Guard a programmatic navigator on the shared unsaved-changes registry.
 *
 * @param perform - Actually navigate to `target` (e.g. `router.push`).
 * @returns The guarded `navigate` plus the pending target and its confirm /
 *   cancel handlers — render an `UnsavedChangesDialog` off `pending !== null`.
 */
export function useGuardedNavigation<T = string>(perform: (target: T) => void): UseGuardedNavigationResult<T> {
  const [pending, setPending] = useState<T | null>(null);

  const navigate = useCallback(
    (target: T) => {
      if (hasUnsavedChanges()) {
        setPending(() => target);
        return;
      }
      perform(target);
    },
    [perform],
  );

  // Read `pending` from the closure, never inside the state updater — an
  // updater runs twice under StrictMode and would navigate twice.
  const confirmLeave = useCallback(() => {
    if (pending !== null) perform(pending);
    setPending(null);
  }, [pending, perform]);

  const cancelLeave = useCallback(() => setPending(null), []);

  return { navigate, pending, confirmLeave, cancelLeave };
}
